import { useState, type FormEvent } from 'react';
import { isAxiosError } from 'axios';
import {
  LINK_IP3_TIMEOUT_DEFAULT_MS,
  LINK_IP3_TIMEOUT_MAX_MS,
  LINK_IP3_TIMEOUT_MIN_MS,
  LinkIp3PhoneFormat,
} from '@reform-lite/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useT } from '@/i18n/use-t';
import { useLocations } from '@/hooks/use-locations';
import {
  useCreateIp3Source,
  useUpdateIp3Source,
  type Ip3Operations,
  type Ip3Settings,
  type Ip3Source,
  type Ip3SourceChange,
} from '@/hooks/use-ip3-sources';

/**
 * Add or edit an integration profile 3 ("CRM loyalty") source (RL-570).
 *
 * The login entered here is stored encrypted and reaches only the store's
 * Link; the form never shows the password back (it says only that one is
 * set), so editing without typing a new one keeps the stored one.
 *
 * The trade-point code and the phone format are not pre-filled: both belong
 * to the store and the CRM, and a guessed one would look customers up under
 * the wrong store or the wrong number without a word.
 */

const PREFIX = 'settings.integrations.ip3';

const OPERATIONS: (keyof Ip3Operations)[] = [
  'cardLookup',
  'phoneLookup',
  'receiptPreference',
  'receiptSubmission',
  'offlineProcessing',
];

/** Whole seconds within the profile's bounds → milliseconds; null when not. */
export function timeoutMsFromSeconds(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const ms = Number(raw.trim()) * 1_000;
  return ms >= LINK_IP3_TIMEOUT_MIN_MS && ms <= LINK_IP3_TIMEOUT_MAX_MS ? ms : null;
}

/** scheme://host:port of a URL; null when it is not one. */
export function originOf(url: string): string | null {
  try {
    return new URL(url.trim()).origin;
  } catch {
    return null;
  }
}

/** Passwords shorter than this are refused (they could not be masked in logs). */
export const IP3_PASSWORD_MIN_LENGTH = 8;

/**
 * Only what the operator changed, plus the `updatedAt` the form was loaded
 * with: the server refuses the edit (409) if the source changed meanwhile,
 * and an unchanged field is never written back over someone else's edit.
 */
export function changedFields(
  source: Ip3Source,
  next: { name: string; username: string; enabled: boolean; settings: Ip3Settings },
): Ip3SourceChange {
  const change: Ip3SourceChange = { expectedUpdatedAt: source.updatedAt };
  if (next.name !== source.name) change.name = next.name;
  if (next.username !== source.username) change.username = next.username;
  if (next.enabled !== source.enabled) change.enabled = next.enabled;
  const settings: Ip3SourceChange['settings'] = {};
  const before = source.settings;
  if (next.settings.baseUrl !== before.baseUrl) settings.baseUrl = next.settings.baseUrl;
  if (next.settings.allowInsecureHttp !== before.allowInsecureHttp) {
    settings.allowInsecureHttp = next.settings.allowInsecureHttp;
  }
  if (next.settings.timeoutMs !== before.timeoutMs) settings.timeoutMs = next.settings.timeoutMs;
  if (next.settings.tradePointCode !== before.tradePointCode) {
    settings.tradePointCode = next.settings.tradePointCode;
  }
  if (next.settings.phoneFormat !== before.phoneFormat) {
    settings.phoneFormat = next.settings.phoneFormat;
  }
  const operations: Partial<Ip3Operations> = {};
  for (const key of OPERATIONS) {
    if ((next.settings.operations[key] ?? false) !== (before.operations[key] ?? false)) {
      operations[key] = next.settings.operations[key] ?? false;
    }
  }
  if (Object.keys(operations).length > 0) settings.operations = operations;
  if (Object.keys(settings).length > 0) change.settings = settings;
  return change;
}

/** True when the URL is plain http — the case the operator must opt into. */
export function isPlainHttp(url: string): boolean {
  return /^http:\/\//i.test(url.trim());
}

function serverMessage(cause: unknown, fallback: string): string {
  if (isAxiosError(cause)) {
    const body = cause.response?.data as { message?: unknown } | undefined;
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

export function Ip3SourceDialog({
  source,
  enabledSibling = null,
  onClose,
}: {
  /** The source being edited; null to add one. */
  source: Ip3Source | null;
  /** The store's enabled source other than `source`, if any. */
  enabledSibling?: Ip3Source | null;
  onClose: () => void;
}): JSX.Element {
  const t = useT('admin_ui');
  const create = useCreateIp3Source();
  const update = useUpdateIp3Source();
  const locations = useLocations({ status: ['active'] });
  const creating = source === null;

  const [locationId, setLocationId] = useState('');
  const [name, setName] = useState(source?.name ?? '');
  const [baseUrl, setBaseUrl] = useState(source?.settings.baseUrl ?? '');
  const [allowInsecureHttp, setAllowInsecureHttp] = useState(
    source?.settings.allowInsecureHttp ?? false,
  );
  const [username, setUsername] = useState(source?.username ?? '');
  const [password, setPassword] = useState('');
  const [timeout, setTimeoutSeconds] = useState(
    String((source?.settings.timeoutMs ?? LINK_IP3_TIMEOUT_DEFAULT_MS) / 1_000),
  );
  const [tradePointCode, setTradePointCode] = useState(source?.settings.tradePointCode ?? '');
  const [phoneFormat, setPhoneFormat] = useState<string>(source?.settings.phoneFormat ?? '');
  const [operations, setOperations] = useState<Ip3Operations>(
    // Nothing pre-ticked: an operation runs only when the operator chose it.
    {
      cardLookup: false,
      phoneLookup: false,
      receiptPreference: false,
      receiptSubmission: false,
      offlineProcessing: false,
      ...source?.settings.operations,
    },
  );
  const [enabled, setEnabled] = useState(source?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;
  const plainHttp = isPlainHttp(baseUrl);
  // The server refuses to send the stored login to another server, or over
  // plain http, unless the password is typed again in the same edit.
  const passwordRequired =
    creating ||
    originOf(baseUrl) !== originOf(source.settings.baseUrl) ||
    (allowInsecureHttp && !source.settings.allowInsecureHttp);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    const timeoutMs = timeoutMsFromSeconds(timeout);
    if (timeoutMs === null) {
      setError(t(`${PREFIX}.form.timeoutInvalid`));
      return;
    }
    if (plainHttp && !allowInsecureHttp) {
      setError(t(`${PREFIX}.form.httpNotAllowed`));
      return;
    }
    if (password !== '' && password.length < IP3_PASSWORD_MIN_LENGTH) {
      setError(t(`${PREFIX}.form.passwordTooShort`));
      return;
    }
    const format = LinkIp3PhoneFormat.safeParse(phoneFormat);
    if (!format.success) {
      setError(t(`${PREFIX}.form.phoneFormatRequired`));
      return;
    }
    const settings = {
      baseUrl: baseUrl.trim(),
      allowInsecureHttp,
      timeoutMs,
      tradePointCode: tradePointCode.trim(),
      phoneFormat: format.data,
      operations,
    };
    try {
      if (creating) {
        await create.mutateAsync({
          locationId,
          name: name.trim(),
          username: username.trim(),
          password,
          enabled,
          settings,
        });
      } else {
        const change = changedFields(source, {
          name: name.trim(),
          username: username.trim(),
          enabled,
          settings,
        });
        // Blank means "keep the stored password", not "clear it".
        if (password !== '') change.password = password;
        // Enabling this one while another is the store's enabled source: the
        // server swaps them in one step, so the store never has none.
        if (change.enabled === true && enabledSibling !== null) {
          change.replaces = enabledSibling.id;
        }
        await update.mutateAsync({ sourceId: source.id, change });
      }
      onClose();
    } catch (cause) {
      setError(serverMessage(cause, t(`${PREFIX}.form.saveError`)));
    }
  }

  const required = [name, baseUrl, username, tradePointCode, phoneFormat, timeout];
  const canSubmit =
    required.every((value) => value.trim() !== '') &&
    (!creating || locationId !== '') &&
    (!passwordRequired || password !== '');

  const field = (
    id: string,
    key: string,
    value: string,
    onChange: (value: string) => void,
    options: { maxLength?: number; inputMode?: 'numeric' | 'url'; hint?: boolean } = {},
  ) => (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{t(`${PREFIX}.form.${key}`)}</Label>
      <Input
        id={id}
        value={value}
        maxLength={options.maxLength}
        inputMode={options.inputMode}
        onChange={(event) => onChange(event.target.value)}
      />
      {options.hint === true && (
        <p className="text-xs text-muted-foreground">{t(`${PREFIX}.form.${key}Hint`)}</p>
      )}
    </div>
  );

  const checkbox = (
    id: string,
    label: string,
    checked: boolean,
    onChange: (v: boolean) => void,
  ) => (
    <div key={id} className="flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <Label htmlFor={id}>{label}</Label>
    </div>
  );

  return (
    <AlertDialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <AlertDialogContent className="max-w-2xl">
        <form onSubmit={submit} className="space-y-4">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {creating ? t(`${PREFIX}.form.createTitle`) : t(`${PREFIX}.form.editTitle`)}
            </AlertDialogTitle>
            <AlertDialogDescription>{t(`${PREFIX}.form.description`)}</AlertDialogDescription>
          </AlertDialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            {creating && (
              <div className="space-y-1.5">
                <Label htmlFor="ip3-location">{t(`${PREFIX}.form.location`)}</Label>
                <Select
                  id="ip3-location"
                  value={locationId}
                  onChange={(event) => setLocationId(event.target.value)}
                >
                  <option value="">{t(`${PREFIX}.form.locationPlaceholder`)}</option>
                  {(locations.data?.items ?? []).map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name} ({location.code})
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {field('ip3-name', 'name', name, setName, { maxLength: 100 })}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ip3-base-url">{t(`${PREFIX}.form.baseUrl`)}</Label>
              <Input
                id="ip3-base-url"
                value={baseUrl}
                maxLength={512}
                inputMode="url"
                placeholder="https://"
                onChange={(event) => setBaseUrl(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t(`${PREFIX}.form.baseUrlHint`)}</p>
            </div>
            {field('ip3-username', 'username', username, setUsername, { maxLength: 256 })}
            <div className="space-y-1.5">
              <Label htmlFor="ip3-password">{t(`${PREFIX}.form.password`)}</Label>
              <Input
                id="ip3-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t(`${PREFIX}.form.passwordHint`)}</p>
              {!creating && !passwordRequired && (
                <p className="text-xs text-muted-foreground">
                  {t(`${PREFIX}.form.passwordSet`)} {t(`${PREFIX}.form.passwordKeepHint`)}
                </p>
              )}
              {!creating && passwordRequired && (
                <p className="text-xs text-destructive">
                  {t(`${PREFIX}.form.passwordRequiredForAddress`)}
                </p>
              )}
            </div>
            {field('ip3-trade-point', 'tradePointCode', tradePointCode, setTradePointCode, {
              maxLength: 64,
              hint: true,
            })}
            <div className="space-y-1.5">
              <Label htmlFor="ip3-phone-format">{t(`${PREFIX}.form.phoneFormat`)}</Label>
              <Select
                id="ip3-phone-format"
                value={phoneFormat}
                onChange={(event) => setPhoneFormat(event.target.value)}
              >
                <option value="">{t(`${PREFIX}.form.phoneFormatPlaceholder`)}</option>
                {LinkIp3PhoneFormat.options.map((value) => (
                  <option key={value} value={value}>
                    {t(`${PREFIX}.form.phoneFormat.${value}`)}
                  </option>
                ))}
              </Select>
            </div>
            {field('ip3-timeout', 'timeoutSeconds', timeout, setTimeoutSeconds, {
              inputMode: 'numeric',
              hint: true,
            })}
            <fieldset className="space-y-2 sm:col-span-2">
              <legend className="text-sm font-medium">{t(`${PREFIX}.form.operations`)}</legend>
              {OPERATIONS.map((operation) =>
                checkbox(
                  `ip3-op-${operation}`,
                  t(`${PREFIX}.form.operation.${operation}`),
                  operations[operation] ?? false,
                  (value) => setOperations((current) => ({ ...current, [operation]: value })),
                ),
              )}
            </fieldset>
            <div className="space-y-1 sm:col-span-2">
              {checkbox(
                'ip3-allow-http',
                t(`${PREFIX}.form.allowInsecureHttp`),
                allowInsecureHttp,
                setAllowInsecureHttp,
              )}
              {(allowInsecureHttp || plainHttp) && (
                <p role="note" className="text-xs text-destructive">
                  {t(`${PREFIX}.form.allowInsecureHttpWarning`)}
                </p>
              )}
            </div>
            <div className="space-y-1 sm:col-span-2">
              {checkbox('ip3-enabled', t(`${PREFIX}.form.enabled`), enabled, setEnabled)}
              <p className="text-xs text-muted-foreground">{t(`${PREFIX}.form.enabledHint`)}</p>
              {source !== null && !source.enabled && enabled && enabledSibling !== null && (
                <p role="note" className="text-xs text-muted-foreground">
                  {t(`${PREFIX}.form.replacesHint`)} {enabledSibling.name}
                </p>
              )}
            </div>
          </div>

          {error !== null && <p className="text-sm text-destructive">{error}</p>}

          <AlertDialogFooter>
            <AlertDialogCancel type="button" onClick={onClose}>
              {t('settings.integrations.cancel')}
            </AlertDialogCancel>
            <Button type="submit" disabled={pending || !canSubmit}>
              {creating ? t(`${PREFIX}.form.createSubmit`) : t(`${PREFIX}.form.editSubmit`)}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
